/**
 * New group.
 *
 * Two steps, as the references describe: tick the participants, then name
 * the group. Splitting it matters — picking twenty people and naming the
 * thing are different jobs, and one screen doing both is where the "who
 * have I already selected?" confusion comes from. The chips along the top
 * of step one exist to answer that.
 *
 * Confirmed from the sources: two steps, a subject of up to 100 characters,
 * an icon set from a camera button, an optional description, and a create
 * action. The subject limit matches chats.name VARCHAR(100) in our own
 * schema, which is a pleasant coincidence rather than a coordination.
 *
 * NOT confirmed, and so not guessed at: where exactly the Next and Create
 * buttons sit — the guides disagree between top-right and bottom — and the
 * participant cap, quoted variously as 100, 512 and 1024. Our server caps
 * at 256 in the createGroup schema; raising it is a one-line change once
 * somebody decides what the number should be.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { contacts as contactService, chats, userMessage } from '@zivapp/shared'
import Avatar from '../components/ui/Avatar.jsx'
import './NewGroup.css'

const SUBJECT_MAX = 100
const DESCRIPTION_MAX = 500

export default function NewGroup () {
  const navigate = useNavigate()

  const [step, setStep] = useState('participants')   // participants | subject
  const [saved, setSaved] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState([])           // user ids, in pick order

  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [isCreating, setCreating] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => contactService.subscribe(rows => {
    setSaved(rows)
    setLoading(false)
  }), [])

  const byId = useMemo(() => {
    const map = new Map()
    for (const c of saved) map.set(c.userId ?? c.user?.id, c)
    return map
  }, [saved])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return saved
    return saved.filter(c =>
      (c.savedName ?? '').toLowerCase().includes(q) ||
      (c.user?.displayName ?? '').toLowerCase().includes(q)
    )
  }, [saved, query])

  const nameOf = id => {
    const c = byId.get(id)
    return c?.savedName || c?.user?.displayName || 'Unknown'
  }

  const toggle = id =>
    setPicked(current =>
      current.includes(id) ? current.filter(x => x !== id) : [...current, id])

  async function create () {
    const name = subject.trim()
    if (!name || !picked.length || isCreating) return

    setCreating(true)
    setError(null)
    try {
      const chat = await chats.createGroup({
        name,
        description: description.trim() || undefined,
        memberIds: picked
      })
      navigate(`/chats/${chat.id}`, { replace: true })
    } catch (err) {
      setError(userMessage(err))
      setCreating(false)
    }
  }

  // ---- step two: name it ----
  if (step === 'subject') {
    return (
      <div className="ng">
        <header className="ng__bar">
          <button className="ng__back" type="button" onClick={() => setStep('participants')}
            aria-label="Back to participants">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="ng__heading"><h1>New group</h1></div>
        </header>

        <div className="ng__subject">
          <button className="ng__icon" type="button" aria-label="Add a group icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 8h2.8l1.4-2.2h7.6L17.2 8H20a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 20 20H4a1.8 1.8 0 0 1-1.8-1.8V9.8A1.8 1.8 0 0 1 4 8Z"
                stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              <circle cx="12" cy="13.8" r="3.4" stroke="currentColor" strokeWidth="1.6" />
            </svg>
          </button>

          <div className="ng__field">
            <input
              className="ng__input"
              value={subject}
              onChange={e => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
              onKeyDown={e => { if (e.key === 'Enter') create() }}
              placeholder="Group subject"
              aria-label="Group subject"
              maxLength={SUBJECT_MAX}
              autoFocus
            />
            <span className="ng__count">{SUBJECT_MAX - subject.length}</span>
          </div>
        </div>

        <label className="ng__description">
          <span className="ziv-sr-only">Group description</span>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
            placeholder="Add a group description (optional)"
            rows={3}
          />
        </label>

        {error && <p className="ng__error" role="alert">{error}</p>}

        <h2 className="ng__section">
          Participants: {picked.length}
        </h2>

        <ul className="ng__chips ng__chips--review">
          {picked.map(id => (
            <li key={id} className="ng__chip">
              <Avatar name={nameOf(id)} url={byId.get(id)?.user?.avatarUrl} size={34} />
              <span>{nameOf(id)}</span>
            </li>
          ))}
        </ul>

        <button
          className="ng__go"
          type="button"
          onClick={create}
          disabled={!subject.trim() || isCreating}
          aria-label="Create group"
        >
          {isCreating ? (
            <span className="ng__go-label">Creating…</span>
          ) : (
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4.5 12.5 9.5 17.5 19.5 7" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>
    )
  }

  // ---- step one: who is in it ----
  return (
    <div className="ng">
      <header className="ng__bar">
        <button className="ng__back" type="button" onClick={() => navigate('/contacts')}
          aria-label="Back to contacts">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="ng__heading">
          <h1>Add participants</h1>
          <p>{picked.length ? `${picked.length} selected` : 'Select who to add'}</p>
        </div>
      </header>

      {/* The chips answer "who have I already picked?" without scrolling
          back through the list to look for ticks. */}
      {picked.length > 0 && (
        <ul className="ng__chips">
          {picked.map(id => (
            <li key={id}>
              <button className="ng__chip" type="button" onClick={() => toggle(id)}
                aria-label={`Remove ${nameOf(id)}`}>
                <Avatar name={nameOf(id)} url={byId.get(id)?.user?.avatarUrl} size={34} />
                <span>{nameOf(id)}</span>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="ng__search">
        <svg className="ng__search-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
          <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
        <input
          className="ng__search-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search contacts"
          aria-label="Search contacts"
        />
      </div>

      <div className="ng__scroll">
        {isLoading ? (
          <p className="ng__state">Loading contacts…</p>
        ) : visible.length === 0 ? (
          <p className="ng__state">
            {query ? 'No matching contacts.' : 'No contacts yet — add someone first.'}
          </p>
        ) : (
          <ul className="ng__list">
            {visible.map(contact => {
              const id = contact.userId ?? contact.user?.id
              const on = picked.includes(id)
              return (
                <li key={id}>
                  <button
                    className={`ng__row${on ? ' is-on' : ''}`}
                    type="button"
                    onClick={() => toggle(id)}
                    aria-pressed={on}
                  >
                    <span className="ng__avatar">
                      <Avatar name={nameOf(id)} url={contact.user?.avatarUrl} size={47} />
                      {on && (
                        <span className="ng__tick" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M5 12.5 10 17.5 19 7" stroke="currentColor"
                              strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      )}
                    </span>

                    <span className="ng__body">
                      <span className="ng__name">{nameOf(id)}</span>
                      <span className="ng__about">
                        {contact.user?.about || 'Hey there! I am on ZIvapp.'}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {picked.length > 0 && (
        <button className="ng__go" type="button" onClick={() => setStep('subject')}
          aria-label="Next">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M5 12h13m0 0-5.5-5.5M18 12l-5.5 5.5" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  )
}
